// src/app/guards/authGuard.service.ts
import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  UrlTree,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Services
import { LoginService, NotifyService } from '../services/service.index';

// Permisos
import { permiso } from '../components/shared/permisos';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private _loginService: LoginService,
    public _notifyService: NotifyService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // 1) Verifica usuario actual desde el backend (SIN subscribe)
    return this._loginService.currentUser().pipe(
      // Si falla la llamada, mándalo a login
      catchError(() => of({ user: { user: ['annm'] } } as any)),

      map((result: any) => {
        const isAnon = result?.user?.user?.[0] === 'annm';

        // Si es anónimo: redirige a login devolviendo UrlTree
        if (isAnon) {
          // permite que /impr* “arrastre” returnUrl si lo necesitas
          const returnUrl = state.url;
          return this.router.createUrlTree(['/login'], {
            queryParams: { url: returnUrl },
          });
        }

        // 2) Si hay sesión, valida permisos por ruta
        const permisosUnicos: string[] =
          JSON.parse(localStorage.getItem('permisosUnicos') || '[]') || [];

        // OJO: route.routeConfig?.path puede ser undefined
        const path = route.routeConfig?.path ?? '';

        // Reglas de permisos (idénticas a las tuyas pero devolviendo UrlTree)
        const deny = (target: string[] = ['/sinPermiso']) =>
          this.router.createUrlTree(target);

        if (
          path === 'ordenes' &&
          !(
            permisosUnicos.includes(permiso.gSifac) ||
            permisosUnicos.includes(permiso.gFnnz) ||
            permisosUnicos.includes(permiso.gContra)
          )
        ) {
          return deny();
        }

        if (
          path === 'proveedores' &&
          !(
            permisosUnicos.includes(permiso.gSifac) ||
            permisosUnicos.includes(permiso.gFnnz) ||
            permisosUnicos.includes(permiso.gContra)
          )
        ) {
          return deny();
        }

        if (
          path === 'documentos' &&
          !(
            permisosUnicos.includes(permiso.gSifac) ||
            permisosUnicos.includes(permiso.gFnnz)
          )
        ) {
          return deny();
        }

        if (
          path === 'solpago' &&
          !(
            permisosUnicos.includes(permiso.gSifac) ||
            permisosUnicos.includes(permiso.gFnnz)
          )
        ) {
          return deny();
        }

        if (path === 'solcomp' && !permisosUnicos.includes(permiso.gPac)) {
          return deny();
        }

        if (path === 'gestautos' && !permisosUnicos.includes(permiso.gAutos)) {
          return deny();
        }

        if (path === 'gestboletos' && !permisosUnicos.includes(permiso.gBoletos)) {
          return deny();
        }

        if (path === 'user' && !permisosUnicos.includes(permiso.user)) {
          return deny();
        }

        if (path === 'contraparte' && !permisosUnicos.includes(permiso.gContra)) {
          return deny();
        }

        // Todo OK
        return true;
      })
    );
  }
}
